// Single source of truth for mobile motion: easing curves, durations,
// and a composer helper. Every transition across the mobile chrome
// should resolve to one of these constants so we don't drift across
// 8 different ad-hoc values (which we did over Sessions 2–10 — the
// S10 review-agent audit found scattered 80/120/160/180/200/220/280
// ms values across 6 files).
//
// Three rules of thumb the constants encode:
//
// 1. **Overshoot springs** (`SPRING_OVERSHOOT`) are for *positional
//    morphs* — tab indicators sliding between tabs, sheets sliding up,
//    future modal entries. The overshoot is what makes them feel
//    "alive" rather than mechanical.
//
// 2. **Snap springs** (`SPRING_SNAP`) are for *press feedback* — FAB,
//    AdjustButton, future button bases. Snappier than overshoot, no
//    perceptible bounce on a 56px target where overshoot looks goofy.
//
// 3. **Standard ease** (`EASE_STANDARD`) is for *quantitative bars* —
//    HP bars, XP, progress indicators, theme color fades. Overshoot
//    here would visually overshoot the actual value, which is bad UX
//    (a negative HP overshoot looks like the bar dipped *lower* than
//    the real number).
//
// Durations align to perceptual buckets, not raw value preferences.
// Don't drift these — if a new component needs a duration outside
// these, name a new constant rather than passing a literal.
//
// Composer:
//   t(['transform', 'opacity'], DUR_BASE, EASE_STANDARD)
//   → 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)'
//
// Reduced-motion: callers gate. The constants here are the motion
// values; `useReducedMotion()` is the per-component gate. Pattern:
//   const reduce = useReducedMotion();
//   transition: reduce ? 'none' : t(['transform'], DUR_SLOW, SPRING_OVERSHOOT),

// === Easing curves ============================================================

// Material-style standard ease. The default for quantitative animations.
export const EASE_STANDARD = 'cubic-bezier(0.4, 0, 0.2, 1)';

// Material decelerate — for elements entering from offscreen. Smoothly
// arrives and lands. Used for sheet enter, modal fade-in.
export const EASE_DECELERATE = 'cubic-bezier(0, 0, 0.2, 1)';

// Material accelerate — for elements leaving the screen entirely.
// Picks up speed as it exits. Used for sheet dismiss when the user
// has already committed (e.g., backdrop tap with intent).
export const EASE_ACCELERATE = 'cubic-bezier(0.4, 0, 1, 1)';

// Overshoot spring — positional morphs. Tab pill sliding, sheet enter,
// FAB position shifts. The classic "iOS feel."
export const SPRING_OVERSHOOT = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// Snap spring — press feedback. Less overshoot than the full spring,
// faster recovery, no perceptible bounce on small elements.
export const SPRING_SNAP = 'cubic-bezier(0.34, 1.20, 0.64, 1)';

// Plain ease-out. Generic deceleration where neither standard nor
// spring fits. Use sparingly — most things should pick one of the above.
export const EASE_OUT = 'ease-out';

// === Durations ================================================================

// Press feedback — visible touch acknowledgment. Below 100ms per
// Apple HIG; AdjustButton uses this.
export const DUR_INSTANT = 80;

// Fast UI response — FAB press recovery, pull-indicator opacity
// transitions. The eye reads it as "immediate but smooth."
export const DUR_FAST = 120;

// Subtle decorative — hover/focus state changes, light fades.
export const DUR_SUBTLE = 160;

// Standard UI animation — sheet scrim, theme color fade, generic
// state transitions. Material 3 baseline.
export const DUR_BASE = 200;

// Quantitative bar transitions (HP, mana, XP). TweenedNumber default.
// Slightly longer than DUR_BASE because the eye tracks the bar's
// midpoint, not its endpoint.
export const DUR_BAR = 220;

// Slower morphs — sheet enter slide, tab indicator slide. The
// overshoot spring lands here.
export const DUR_SLOW = 280;

// Cinematic — reveal sequences, scene fades. For deliberate moments.
// Don't use on UI chrome.
export const DUR_CINEMATIC = 450;

// === Composer ================================================================

/**
 * Build a CSS `transition` string from properties + duration + curve.
 * Callers gate with `useReducedMotion()`:
 *   const reduce = useReducedMotion();
 *   transition: reduce ? 'none' : t(['transform'], DUR_SLOW, SPRING_OVERSHOOT),
 *
 * @param {string[]} props - CSS properties to transition (e.g. ['transform', 'opacity']).
 * @param {number} duration - Duration in milliseconds.
 * @param {string} curve - Easing curve string.
 * @returns {string} CSS transition value.
 */
export const t = (props, duration = DUR_BASE, curve = EASE_STANDARD) => {
  if (!Array.isArray(props) || props.length === 0) return 'none';
  return props.map((p) => `${p} ${duration}ms ${curve}`).join(', ');
};

// === Press-state visual constants =============================================

// Reusable visual transformation for "tap-down" press states. Apply
// only when the button is actively pressed AND reduced-motion is off:
//   ...(pressed && !reduce ? PRESSED_TRANSFORM : null),
export const PRESSED_TRANSFORM = {
  transform: 'scale(0.97)',
  filter: 'brightness(1.1)',
};

// Stronger press for combat-critical actions where the user benefits
// from a more pronounced confirmation feel.
export const PRESSED_TRANSFORM_STRONG = {
  transform: 'scale(0.95)',
  filter: 'brightness(1.15)',
};
